import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { BiSolidError } from "react-icons/bi";
import { FaEye, FaPlus } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { IoCloseSharp } from "react-icons/io5";
import { MdDeleteForever, MdOutlineEdit, MdPayment } from "react-icons/md";
import "react-loading-skeleton/dist/skeleton.css";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ActionMenu from "../../../component/ActionMenu";
import SkeletonLoading from "../../../component/SkeletonLoading";
import Tooltip from "../../../component/Tooltip";
import i18n from "../../../i8next";
import {
  useGetParentPaymentQuery,
  useParentPaymentMutation,
} from "../../../store/api/parent/registrationSlice";
import {
  useDeleteRecordMutation,
  useGetRecordDetailsQuery,
} from "../../../store/api/school/PraentSlice";
import {
  formatDateToLocal,
  handleApiError,
} from "../../../utils/dateAndTimeConversion";
import "../../AddVehicle.scss";

// By Parent Listing Students
const Studenst = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [students, setStudents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role;
  const isParent = role === "parent";
  const isAdmin = role === "super_admin";

  const [deleteStudent, { isLoading: isDeletingStudent }] =
    useDeleteRecordMutation();

  // Payment modal state
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingStudent, setPayingStudent] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    currency: "USD",
    paymentType: "full_payment",
  });

  const sid = isParent ? user?.id : id;
  // Get parent details query
  const {
    data,
    isSuccess,
    isLoading: isGetLoading,
    refetch,
  } = useGetRecordDetailsQuery(`${sid}/students`);

  // Parent payment summary
  // parent: no param needed (backend uses req.user.id)
  // school/vendor: pass parent id from URL params
  const { data: paymentData } = useGetParentPaymentQuery(
    isParent ? undefined : id,
    { skip: !isParent && !id },
  );
  const paymentSummary = paymentData?.data?.summary || null;

  // Map student_id → full breakdown object (keyed by student_id so multiple
  // students on the same bus route don't collide — bus_fee_id is not unique)
  const feeBreakdown = paymentData?.data?.summary?.fee_breakdown || [];
  const feeMap = Object.fromEntries(
    feeBreakdown
      .filter((fb) => fb.student_id)
      .map((fb) => [fb.student_id, fb]),
  );
  const paymentCurrency = paymentData?.data?.payments?.[0]?.currency || "USD";
  const currencySymbol = paymentCurrency === "CAD" ? "C$" : "$";

  const [parentPayment, { isLoading: isPaymentLoading }] =
    useParentPaymentMutation();

  const handlePaySubmit = async () => {
    toast.dismiss();
    try {
      const studentFee = parseFloat(payingStudent?.fee || 0);
      const payload = { ...paymentFormData };
      if (payingStudent?.student_id || payingStudent?.id) {
        payload.studentIds = [payingStudent.student_id || payingStudent.id];
      }
      if (payload.paymentType === "full_payment") {
        payload.paymentType = "partial_payment";
        payload.partialAmount = parseFloat(studentFee.toFixed(2));
      } else if (payload.paymentType === "partial_payment") {
        payload.partialAmount = parseFloat((studentFee / 3).toFixed(2));
      } else if (payload.paymentType === "deposit") {
        payload.partialAmount = 1000;
      }
      const response = await parentPayment(payload).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        window.open(response.data.iframe_url, "_blank");
        setShowPayModal(false);
      }
    } catch (error) {
      handleApiError(error, t);
    }
  };

  useEffect(() => {
    if (isSuccess && data?.data) {
      setStudents(data.data);
    }
  }, [data, isSuccess]);

  // Format status badge
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return <span className="badge bg-success">Active</span>;
      case "pending":
        return <span className="badge bg-warning">Pending</span>;
      case "inactive":
        return <span className="badge bg-secondary">Inactive</span>;
      default:
        return <span className="badge bg-info">N/A</span>;
    }
  };

  // Format disability status
  const getDisabilityStatus = (isDisabled) => {
    return isDisabled === 1 ? (
      <span className="badge bg-danger">Yes</span>
    ) : (
      <span className="badge bg-success">No</span>
    );
  };

  // Handle eye button click to show modal
  const handleShowDetails = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
  };

  const handleDeleteStudent = async () => {
    toast.dismiss();
    try {
      const response = await deleteStudent(
        `student/${deleteTarget.student_id || deleteTarget.id}`,
      ).unwrap();
      toast.success(response.message || t("student_deleted_success"));
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      toast.error(err.data?.message || t("something_went_wrong"));
    }
  };

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between">
        <div className="mb-2 total">
          {t("total")} {t("students")}: {students?.length}
        </div>
        {isParent && (
          <div className="d-flex justify-content-end mb-2">
            <NavLink
              to="add"
              className="page-add-btn d-flex align-items-center gap-2"
            >
              <FaPlus /> {t("add")} {t("kids")}
            </NavLink>
          </div>
        )}
      </div>
      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>
                {t("Sr")}.{t("no")}.
              </th>
              <th className="text-nowrap">
                {t("student")} {t("name")}
              </th>
              <th>
                {t("roll")} {t("number")}
              </th>
              <th
                className="text-nowrap"
                style={{ minWidth: "400px", width: "auto" }}
              >
                {t("address")}
              </th>
              <th>{t("fee")}</th>
              <th>{t("status")}</th>
              <th>{t("disabled")}</th>
              <th className="text-nowrap">
                {t("doctor")} {t("phone")}
              </th>
              <th>{t("created_at")}</th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isGetLoading ? (
              <SkeletonLoading MainLength={5} length={10} />
            ) : students?.length > 0 ? (
              students?.map((item, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td className="text-nowrap">{item?.full_name}</td>
                  <td className="text-nowrap">{item?.roll_number}</td>
                  <td style={{ minWidth: "400px" }}>{item?.student_street}</td>
                  <td className="text-nowrap">
                    {feeMap[item.student_id] !== undefined
                      ? `${currencySymbol}${parseFloat(feeMap[item.student_id].fee).toFixed(2)}`
                      : `$${parseFloat(item?.fee || 0).toFixed(2)}`}
                  </td>
                  <td className="text-nowrap">
                    {getStatusBadge(item?.status)}
                  </td>
                  <td className="text-nowrap">
                    <div className="d-flex align-items-center gap-2">
                      {getDisabilityStatus(item?.is_disabled)}
                    </div>
                  </td>
                  <td className="text-nowrap">{item?.dr_phone_no || "N/A"}</td>
                  <td className="text-nowrap">
                    {item?.created_at
                      ? formatDateToLocal(item.created_at)
                      : "N/A"}
                  </td>

                  <td>
                    <ActionMenu
                      actions={[
                        {
                          label: t("edit"),
                          icon: <MdOutlineEdit size={16} />,
                          onClick: () =>
                            navigate(`/manage-kids/edit/${item.student_id}`),
                          hidden: !isParent,
                        },
                        {
                          label: `${t("view")} ${t("details")}`,
                          icon: <FaEye size={15} />,
                          onClick: () => handleShowDetails(item),
                        },
                        {
                          label: `${t("pay")} ${t("fee")}`,
                          icon: <MdPayment size={15} />,
                          onClick: () => {
                            setPayingStudent(item);
                            setPaymentFormData({
                              currency: "USD",
                              paymentType: "full_payment",
                            });
                            setShowPayModal(true);
                          },
                          hidden: !isParent || item?.status !== "pending",
                        },
                        {
                          label: t("btns.delete"),
                          icon: <MdDeleteForever size={16} />,
                          onClick: () => setDeleteTarget(item),
                          hidden: !isAdmin,
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="9">
                  <div>
                    <span>
                      {t("no")} {t("student")} {t("found")}
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Disability Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton className="border-0 justify-content-between">
          <Modal.Title className="h5">{t("details")}</Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="pt-0"
          style={{
            maxHeight: "calc(80vh - 120px)", // Fixed max height
            overflowY: "auto", // Auto overflow for vertical scroll
            paddingTop: "1rem",
          }}
        >
          {selectedStudent && (
            <div className="p-2">
              {/* <div className="mb-4">
                <h5 className="my-2 fw-semibold">
                  Student: {selectedStudent.full_name}
                </h5>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <span className="text-muted">
                    Roll No: <strong>{selectedStudent.roll_number}</strong>
                  </span>
                  <span className="badge bg-secondary">
                    Fee: ${parseFloat(selectedStudent?.fee || 0).toFixed(2)}
                  </span>
                </div>
              </div> */}

              <div className="mb-4 mt-2">
                <h6 className="border-bottom pb-2 mb-3 fw-semibold">
                  {t("disability")} {t("information")}
                </h6>

                <div className="row mb-3 align-items-center">
                  <div className="col-4">
                    <strong className="text-muted">{t("status")}:</strong>
                  </div>
                  <div className="col-8">
                    {getDisabilityStatus(selectedStudent.is_disabled)}
                  </div>
                </div>

                {selectedStudent.disability_notes && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong className="text-muted d-block mb-2">
                        {t("disability")} {t("notes")}:
                      </strong>
                      <div
                        className="p-3 bg-light rounded"
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          fontSize: "0.9rem",
                        }}
                      >
                        {selectedStudent.disability_notes}
                      </div>
                    </div>
                  </div>
                )}

                {selectedStudent.dr_phone_no && (
                  <div className="row align-items-center">
                    <div className="col-4">
                      <strong className="text-muted">
                        {t("Doctor's")} {t("phone")}:
                      </strong>
                    </div>
                    <div className="col-8">
                      <a
                        href={`tel:${selectedStudent.dr_phone_no}`}
                        className="text-decoration-none d-flex align-items-center gap-2"
                      >
                        <span className="badge bg-dark">
                          {selectedStudent.dr_phone_no}
                        </span>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-3 border-top">
                <div className="row mb-2">
                  <div className="col-4">
                    <strong className="text-muted">
                      {t("student")} {t("status")}:
                    </strong>
                  </div>
                  <div className="col-8">
                    {getStatusBadge(selectedStudent.status)}
                  </div>
                </div>

                <div className="row">
                  <div className="col-4">
                    <strong className="text-muted">{t("address")}:</strong>
                  </div>
                  <div className="col-8">
                    <small className="text-muted">
                      {selectedStudent?.student_street}
                    </small>
                  </div>
                </div>
              </div>

              {/* Payment detail — only for parent when breakdown exists */}
              {feeMap[selectedStudent?.student_id] &&
                (() => {
                  const fb = feeMap[selectedStudent.student_id];
                  const pr = fb.pro_rate || {};
                  return (
                    <div className="mt-4 pt-3 border-top">
                      <h6 className="fw-semibold mb-3">
                        {t("fee")} {t("details")}
                      </h6>

                      <div
                        className="rounded-3 p-3"
                        style={{
                          backgroundColor: "rgba(240,178,65,0.07)",
                          border: "1px solid rgba(240,178,65,0.25)",
                        }}
                      >
                        {/* Annual fee */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Annual Fee</span>
                          <span className="fw-semibold">
                            {currencySymbol}
                            {parseFloat(fb.annual_fee || 0).toFixed(2)}
                          </span>
                        </div>

                        {/* Monthly fee */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Monthly Fee</span>
                          <span className="fw-semibold">
                            {currencySymbol}
                            {parseFloat(pr.monthlyFee || 0).toFixed(2)}
                          </span>
                        </div>

                        {/* Total months */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Total Months</span>
                          <span className="fw-semibold">
                            {pr.totalMonths || "—"}
                          </span>
                        </div>

                        {/* Remaining months */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">
                            Remaining Months
                          </span>
                          <span className="fw-semibold">
                            {pr.remainingMonths || "—"}
                          </span>
                        </div>

                        {/* Divider */}
                        <div
                          className="border-top my-2"
                          style={{ borderColor: "rgba(240,178,65,0.25)" }}
                        />

                        {/* Paid and Remaining — computed by backend per payment type */}
                        {fb.paid !== undefined && (
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-semibold">Paid Fee</span>
                            <span
                              style={{
                                color: "#22c55e",
                                fontWeight: 700,
                                fontSize: "1.05rem",
                              }}
                            >
                              {currencySymbol}
                              {parseFloat(fb.paid).toFixed(2)}
                            </span>
                          </div>
                        )}
                        {fb.remaining !== undefined && (
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-semibold">Remaining Fee</span>
                            <span
                              style={{
                                color: "#ef4444",
                                fontWeight: 700,
                                fontSize: "1.05rem",
                              }}
                            >
                              {currencySymbol}
                              {parseFloat(fb.remaining).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            className="px-4"
          >
            {t("btns.close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pay Fee Modal */}
      <Modal
        show={showPayModal}
        centered
        onHide={() => setShowPayModal(false)}
        size="md"
      >
        <Modal.Header closeButton className="border-0 justify-content-between">
          <Modal.Title className="fw-semibold">
            {t("pay")} {t("fee")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {/* Student Fee Summary */}
          {payingStudent && (
            <div
              className="p-3 rounded mb-4"
              style={{
                backgroundColor: "rgba(240, 178, 65, 0.08)",
                border: "1px solid rgba(240, 178, 65, 0.3)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="text-muted">{t("total")} {t("due")}</span>
                  <div className="small text-muted mt-1">{payingStudent.full_name}</div>
                </div>
                <span style={{ color: "#f0b241", fontWeight: 700, fontSize: "1.3rem" }}>
                  {paymentFormData.currency === "USD" ? "$" : "C$"}
                  {parseFloat(payingStudent.fee || 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Currency */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-2">
              {t("select")} {t("currency")}
            </h6>
            <div className="d-flex gap-3">
              {["USD", "CAD"].map((c) => (
                <div
                  key={c}
                  className="border rounded-3 px-3 py-2 d-flex align-items-center gap-2"
                  style={{
                    cursor: "pointer",
                    borderColor:
                      paymentFormData.currency === c ? "#f0b241" : "#dee2e6",
                    backgroundColor:
                      paymentFormData.currency === c
                        ? "rgba(240, 178, 65, 0.08)"
                        : "transparent",
                  }}
                  onClick={() =>
                    setPaymentFormData((prev) => ({ ...prev, currency: c }))
                  }
                >
                  <div className="pm-radio-wrap">
                    <div
                      className={`pm-radio-dot ${paymentFormData.currency === c ? "pm-dot-checked" : ""}`}
                    />
                  </div>
                  <span
                    className={
                      paymentFormData.currency === c ? "fw-semibold" : ""
                    }
                  >
                    {c === "USD" ? t("usd_currency") : t("cad_currency")}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Type */}
          <div className="mb-2">
            <h6 className="fw-semibold mb-3">
              {t("payment")} {t("type")}
            </h6>
            <div className="row g-3">
              {/* Full */}
              <div className="col-6">
                <div
                  className="border rounded-3 p-3 h-100"
                  style={{
                    cursor: "pointer",
                    borderColor:
                      paymentFormData.paymentType === "full_payment"
                        ? "#f0b241"
                        : "#dee2e6",
                    backgroundColor:
                      paymentFormData.paymentType === "full_payment"
                        ? "rgba(240, 178, 65, 0.08)"
                        : "transparent",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() =>
                    setPaymentFormData((prev) => ({
                      ...prev,
                      paymentType: "full_payment",
                    }))
                  }
                >
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <div className="pm-radio-wrap">
                      <div
                        className={`pm-radio-dot ${paymentFormData.paymentType === "full_payment" ? "pm-dot-checked" : ""}`}
                      />
                    </div>
                    <span className="fw-semibold small">
                      {t("full")} {t("payment")}
                    </span>
                  </div>
                  <div style={{ color: "#f0b241", fontWeight: 700 }}>
                    {paymentFormData.currency === "USD" ? "$" : "C$"}
                    {parseFloat(payingStudent?.fee || 0).toFixed(2)}
                  </div>
                  <div className="d-flex align-items-center gap-1 mt-2">
                    <span style={{ color: "#f0b241", fontWeight: 700, fontSize: "0.85rem" }}>✓</span>
                    <span className="small text-muted">{t("no")} {t("additional")} {t("fees")}</span>
                  </div>
                </div>
              </div>
              {/* Partial */}
              <div className="col-6">
                <div
                  className="border rounded-3 p-3 h-100"
                  style={{
                    cursor: "pointer",
                    borderColor: paymentFormData.paymentType === "partial_payment" ? "#f0b241" : "#dee2e6",
                    backgroundColor: paymentFormData.paymentType === "partial_payment" ? "rgba(240, 178, 65, 0.08)" : "transparent",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => setPaymentFormData((prev) => ({ ...prev, paymentType: "partial_payment" }))}
                >
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <div className="pm-radio-wrap">
                      <div className={`pm-radio-dot ${paymentFormData.paymentType === "partial_payment" ? "pm-dot-checked" : ""}`} />
                    </div>
                    <span className="fw-semibold small">{t("partial")} {t("payment")}</span>
                  </div>
                  <div style={{ color: "#f0b241", fontWeight: 700 }}>
                    {paymentFormData.currency === "USD" ? "$" : "C$"}
                    {parseFloat(((payingStudent?.fee || 0) / 3).toFixed(2))}
                    <span className="text-muted small fw-normal ms-1">× 3</span>
                  </div>
                  <div className="d-flex align-items-center gap-1 mt-2">
                    <span style={{ color: "#f0b241", fontWeight: 700, fontSize: "0.85rem" }}>✓</span>
                    <span className="small text-muted">{t("flexible")} {t("payment")} {t("plan")}</span>
                  </div>
                </div>
              </div>
              {/* Deposit */}
              {parseFloat(payingStudent?.fee || 0) > 1000 && (
                <div className="col-6">
                  <div
                    className="border rounded-3 p-3 h-100"
                    style={{
                      cursor: "pointer",
                      borderColor: paymentFormData.paymentType === "deposit" ? "#f0b241" : "#dee2e6",
                      backgroundColor: paymentFormData.paymentType === "deposit" ? "rgba(240, 178, 65, 0.08)" : "transparent",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => setPaymentFormData((prev) => ({ ...prev, paymentType: "deposit" }))}
                  >
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <div className="pm-radio-wrap">
                        <div className={`pm-radio-dot ${paymentFormData.paymentType === "deposit" ? "pm-dot-checked" : ""}`} />
                      </div>
                      <span className="fw-semibold small">{t("deposit")}</span>
                    </div>
                    <div style={{ color: "#f0b241", fontWeight: 700 }}>
                      {paymentFormData.currency === "USD" ? "$" : "C$"}1,000.00
                    </div>
                    <div className="d-flex align-items-center gap-1 mt-2">
                      <span style={{ color: "#f0b241", fontWeight: 700, fontSize: "0.85rem" }}>✓</span>
                      <span className="small text-muted">{t("secure_your_spot")}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          className="border-top pt-3"
          style={{ borderColor: "rgba(240, 178, 65, 0.3)" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowPayModal(false)}
            className="px-4"
          >
            {t("btns.cancel")}
          </Button>
          <button
            onClick={handlePaySubmit}
            disabled={isPaymentLoading}
            className="btn px-4"
            style={{
              backgroundColor: "#f0b241",
              borderColor: "#f0b241",
              color: "#fff",
              fontWeight: 600,
            }}
          >
            {isPaymentLoading ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : null}
            {t("pay")} {paymentFormData.currency === "USD" ? "$" : "C$"}
            {parseFloat(
              paymentFormData.paymentType === "full_payment"
                ? payingStudent?.fee || 0
                : paymentFormData.paymentType === "deposit"
                  ? 1000
                  : (payingStudent?.fee || 0) / 3,
            ).toFixed(2)}
          </button>
        </Modal.Footer>

        <style>{`
          .pm-radio-wrap { display: flex; align-items: center; }
          .pm-radio-dot {
            width: 16px; height: 16px;
            border: 2px solid #ccc; border-radius: 50%;
            position: relative; transition: all 0.2s ease; flex-shrink: 0;
          }
          .pm-radio-dot.pm-dot-checked { border-color: #f0b241; }
          .pm-radio-dot.pm-dot-checked::after {
            content: ''; position: absolute;
            top: 50%; left: 50%; transform: translate(-50%, -50%);
            width: 8px; height: 8px;
            background-color: #f0b241; border-radius: 50%;
          }
        `}</style>
      </Modal>

      {/* Delete Student Confirmation Modal */}
      <Modal show={!!deleteTarget} centered onHide={() => setDeleteTarget(null)}>
        <Button className="closePopup" onClick={() => setDeleteTarget(null)}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div className="text-center mb-3 errorIcon">
            <BiSolidError />
          </div>
          <div className="text-center mb-2 deleteHd">
            {t("confirmDelete")}
          </div>
          {deleteTarget?.full_name && (
            <div className="text-center mb-3" style={{ fontSize: "0.9rem" }}>
              <strong>{deleteTarget.full_name}</strong>
            </div>
          )}
          <div
            className="mb-4 p-2 text-center"
            style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              fontSize: "0.82rem",
              color: "#856404",
            }}
          >
            ⚠️ {t("delete_student_warning")}
          </div>
          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeletingStudent}
            >
              {t("btns.cancel")}
            </Button>
            <Button
              className="smallBtn auto"
              onClick={handleDeleteStudent}
              disabled={isDeletingStudent}
            >
              {isDeletingStudent ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : (
                t("btns.delete")
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Studenst;
